import { useDraggable } from '@dnd-kit/core';
import { FaBell, FaProjectDiagram, FaUsers, FaUserCircle, FaCalendarAlt } from 'react-icons/fa';
import SlaCountdownChip from './SlaCountdownChip';

/**
 * Returns true when the given action item type should show IT-related features
 * (SLA countdown chip, ticket reference, etc.). Mirrors `isItTicketType()` in
 * ActionItemDetailPage.jsx and `SlaInitializationService.IsItTicket()` on the backend.
 */
const isItType = (type) => {
    if (!type) return false;
    const lower = type.toLowerCase();
    if (lower.startsWith('it-') || lower.startsWith('it ')) return true;
    return lower.includes('it support') || lower.includes('it service') || lower.includes('it incident');
};

/**
 * KanbanCard Component
 * Compact, information-dense Kanban card for action items.
 * Shows priority bar + title on line 1, status + meta on line 2, assignee + ticket on line 3.
 * For IT tickets a live SLA countdown chip is added to line 3.
 * Full detail is revealed in the ActionItemDetail modal on click.
 */
const KanbanCard = ({
    item,
    propertyName,
    onClick,
    needsMyAttention,
    getItemIcon,
    getItemDisplayText,
    getUserDisplayName,
    formatCardDate,
    getProjectName,
    navigate,
    allUsers,
    isAssignedToMyDepartment
}) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useDraggable({
        id: item.id.toString()
    });

    const assignedUser = allUsers.find(u => (u.userID || u.id) === item.assignedToUserID);
    const assignedUserName = getUserDisplayName(assignedUser);
    const itemNeedsAttention = needsMyAttention(item);
    const isDepartmentAssigned = isAssignedToMyDepartment && isAssignedToMyDepartment(item);
    const itemIcon = getItemIcon(item);
    const showSla = isItType(item.actionItemsType) && item.sla;

    const priorityColors = {
        'Critical': '#e74c3c',
        'High': '#e67e22',
        'Medium': '#f1c40f',
        'Low': '#3498db'
    };
    const priorityColor = priorityColors[item.priority] || '#95a5a6';

    const statusSlug = (item.status || '').replace(/\s+/g, '').toLowerCase();
    const projectName = item.projectId ? getProjectName(item.projectId) : null;

    return (
        <div
            ref={setNodeRef}
            style={{
                borderLeft: `3px solid ${priorityColor}`,
                transition,
                transform: transform ? `translate3d(${transform.x}px,${transform.y}px,0)` : undefined,
                opacity: isDragging ? 0.6 : 1
            }}
            {...attributes}
            {...listeners}
            onClick={onClick}
            className={`action-item-card compact ${itemNeedsAttention ? 'needs-attention' : ''} ${isDepartmentAssigned ? 'department-assigned' : ''}`}
            title={getItemDisplayText(item)?.props?.children || item.description || item.title || ''}
        >
            {/* Attention / department indicators */}
            {itemNeedsAttention && (
                <span className="card-indicator attention" title="Needs your attention">
                    <FaBell />
                </span>
            )}
            {isDepartmentAssigned && !itemNeedsAttention && (
                <span className="card-indicator department" title="Assigned to your department">
                    <FaUsers />
                </span>
            )}

            {/* Row 1: icon + title */}
            <div className="card-row card-row-title">
                <i className={`fas ${itemIcon} card-icon`} title={item.templateKey || item.actionItemsType || 'Action Item'}></i>
                <span className="card-title-text">{getItemDisplayText(item)}</span>
            </div>

            {/* Row 2: status chip + property + project + due/created */}
            <div className="card-row card-row-meta">
                <span className={`status-chip status-${statusSlug}`}>{item.status}</span>
                {propertyName && <span className="meta-pill" title={propertyName}>{propertyName}</span>}
                {projectName && (
                    <span
                        className="meta-pill project-pill"
                        onClick={(e) => { e.stopPropagation(); navigate(`/projects/${item.projectId}`); }}
                        title={`Project: ${projectName}`}
                    >
                        <FaProjectDiagram /> {projectName}
                    </span>
                )}
                {item.createdDate && (
                    <span className="meta-date" title={`Created ${formatCardDate(item.createdDate)}`}>
                        <FaCalendarAlt /> {formatCardDate(item.createdDate)}
                    </span>
                )}
            </div>

            {/* Row 3: assignee + SLA countdown (IT tickets) + ticket id */}
            <div className="card-row card-row-footer">
                <span className="card-assignee">
                    {isDepartmentAssigned ? (
                        <><FaUsers /> Department</>
                    ) : (
                        <><FaUserCircle /> {assignedUserName || (item.assignedToUserID ? '…' : 'Unassigned')}</>
                    )}
                </span>
                {/* Live SLA countdown chip — only rendered for IT tickets with tracked SLA */}
                {showSla && <SlaCountdownChip sla={item.sla} variant="card" />}
                <span className="card-ticket">#{item.id}</span>
            </div>
        </div>
    );
};

export default KanbanCard;
